import React, { useRef } from 'react';
import { CSSObject } from 'fela';
import { Transition } from 'react-transition-group';
import {
  TransitionProps,
  TransitionStatus,
  UNMOUNTED,
} from 'react-transition-group/Transition';
import { useFela } from '../../index';

type CollapseProps = TransitionProps & { timeout: number };

const resolveElementStyles = ({
  state,
  timeout,
}: {
  state: TransitionStatus;
  timeout: number;
}): CSSObject => {
  const defaultStyles = {
    minHeight: 0,
    transition: `height ease-in-out ${timeout}ms`,
  };

  const stateStyles = {
    entering: {
      overflow: 'hidden',
    },
    entered: {
      overflow: 'visible',
    },
    exiting: {
      overflow: 'hidden',
    },
    exited: {
      overflow: 'hidden',
      visibility: 'hidden' as CSSObject['visibility'],
    },
  };

  const styles = {
    ...defaultStyles,
    ...stateStyles[state as Exclude<TransitionStatus, typeof UNMOUNTED>],
  };

  return styles;
};

const wrapperStyle = (): CSSObject => ({
  display: 'flex',
});

/**
 * The `Collapse` component is used for animating the height of its children (collapsing).
 *
 * Most commonly it's used to animate the mounting and unmounting, but can also be used to describe in-place transition states as well.
 * It uses react-transition-group internally and can provide a rich set of event callbacks which we can use to handle enter and exit states.
 */
const Collapse = ({
  children,
  in: inProp,
  onEnter,
  onEntered,
  onEntering,
  onExit,
  onExited,
  onExiting,
  timeout,
  ...otherProps
}: CollapseProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { css } = useFela();

  const handleEnter = (node: HTMLElement, isAppearing: boolean) => {
    node.style.height = '0';

    if (onEnter) {
      onEnter(node, isAppearing);
    }
  };

  const handleEntering = (node: HTMLElement, isAppearing: boolean) => {
    const wrapperHeight = wrapperRef.current
      ? wrapperRef.current.clientHeight
      : 0;
    node.style.height = `${wrapperHeight}px`;

    if (onEntering) {
      onEntering(node, isAppearing);
    }
  };

  const handleEntered = (node: HTMLElement, isAppearing: boolean) => {
    node.style.height = 'auto';

    if (onEntered) {
      onEntered(node, isAppearing);
    }
  };

  const handleExit = (node: HTMLElement) => {
    const wrapperHeight = wrapperRef.current
      ? wrapperRef.current.clientHeight
      : 0;
    node.style.height = `${wrapperHeight}px`;

    if (onExit) {
      onExit(node);
    }
  };

  const handleExiting = (node: HTMLElement) => {
    node.style.height = '0';

    if (onExiting) {
      onExiting(node);
    }
  };

  const handleExited = (node: HTMLElement) => {
    if (onExited) {
      onExited(node);
    }
  };

  return (
    <Transition
      in={inProp}
      timeout={timeout}
      onEnter={handleEnter}
      onEntering={handleEntering}
      onEntered={handleEntered}
      onExit={handleExit}
      onExiting={handleExiting}
      onExited={handleExited}
      {...otherProps}>
      {state => {
        // FIXME: 🤔Without calculating `clientHeight` here, the `Collapse` height transition is not executing correctly.
        (() => (wrapperRef.current ? wrapperRef.current.clientHeight : 0))();
        const elementStyles = resolveElementStyles({
          state,
          timeout,
        });
        return (
          <div data-dmid="collapse" className={css(elementStyles)}>
            <div
              ref={wrapperRef}
              data-dmid="collapse-wrapper"
              className={css(wrapperStyle)}>
              {children}
            </div>
          </div>
        );
      }}
    </Transition>
  );
};

export default React.memo(Collapse);
